import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

export interface AppNotification {
  id: string;
  event_id: string;
  title: string;
  body: string;
  created_at: string;
  read_at: string | null;
}

interface NotificationsState {
  items: AppNotification[];
  unread: number;
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
  markRead: (id?: string) => Promise<void>;
}
const NotificationsContext = createContext<NotificationsState | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const userId = useRef(user?.id);
  userId.current = user?.id;
  const refresh = useCallback(async () => {
    const id = user?.id;
    if (!id) return;
    try {
    const [list, count] = await Promise.all([
      supabase.from('app_notifications').select('id,event_id,title,body,created_at,read_at').eq('user_id', id).order('created_at', { ascending: false }).limit(50),
      supabase.from('app_notifications').select('id', { count: 'exact', head: true }).eq('user_id', id).is('read_at', null),
    ]);
    if (userId.current !== id) return;
    if (list.error || count.error) setError('Notifications could not be loaded. Try again.');
    else { setItems(list.data || []); setUnread(count.count || 0); setError(''); }
    } catch {
      if (userId.current === id) setError('Notifications could not be loaded. Try again.');
    } finally {
      if (userId.current === id) setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    setItems([]); setUnread(0); setError(''); setLoading(Boolean(user?.id));
    if (!user?.id) return;
    void refresh();
    const channel = supabase.channel(`notifications-${user.id}`).on('postgres_changes', {
      event: '*', schema: 'public', table: 'app_notifications', filter: `user_id=eq.${user.id}`,
    }, () => void refresh()).subscribe();
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    const timer = window.setInterval(onVisible, 60000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      void supabase.removeChannel(channel);
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [user?.id, refresh]);

  const markRead = async (id?: string) => {
    if (!user?.id) return;
    let query = supabase.from('app_notifications').update({ read_at: new Date().toISOString() }).eq('user_id', user.id).is('read_at', null);
    if (id) query = query.eq('id', id);
    const { error } = await query;
    if (error) throw new Error('Could not mark notifications as read.');
    await refresh();
  };
  return <NotificationsContext.Provider value={{ items, unread, loading, error, refresh, markRead }}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  return useContext(NotificationsContext);
}
